import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eyebrow } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { getBlog } from "@/lib/api";

const CATS = ["All", "Art", "Creativity", "Artists", "Achievements", "Community"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlog().then((d) => { setPosts(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = active === "All" ? posts : posts.filter((p) => p.category === active);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-20">
          <Eyebrow>Blog</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold tracking-tight sm:text-6xl">Notes on art, creativity and community.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">The editorial arm of YTCA — ideas that support the movement, not generic how-tos.</p>
        </div>
      </section>

      <div className="ytca-container py-10">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${active === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-12 text-muted-foreground">Loading posts…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-12 text-muted-foreground">No posts in this category yet.</p>
        ) : (
          <>
            {featured && (
              <Reveal className="mt-8">
                <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="group block">
                  <Card className="card-lift grid overflow-hidden rounded-3xl border-border bg-card md:grid-cols-2">
                    <img src={featured.image} alt={featured.title} className="aspect-[16/10] w-full object-cover md:aspect-auto md:h-full" />
                    <div className="flex flex-col justify-center p-8">
                      <Badge variant="secondary" className="w-fit rounded-full">{featured.category}</Badge>
                      <h2 className="mt-4 font-display text-2xl font-semibold sm:text-3xl">{featured.title}</h2>
                      <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>
                      <div className="mt-4 text-xs text-muted-foreground">{featured.author} · {featured.read_time}</div>
                    </div>
                  </Card>
                </Link>
              </Reveal>
            )}
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.05}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block h-full">
                    <Card className="card-lift h-full overflow-hidden rounded-2xl border-border bg-card hover:border-primary/40">
                      <img src={p.image} alt={p.title} className="aspect-[16/10] w-full object-cover" />
                      <div className="p-5">
                        <Badge variant="secondary" className="rounded-full">{p.category}</Badge>
                        <h3 className="mt-3 font-display text-lg font-semibold">{p.title}</h3>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
                        <div className="mt-3 text-xs text-muted-foreground">{p.read_time}</div>
                      </div>
                    </Card>
                  </Link>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
